import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiZap, FiMail, FiLock, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import { isSupabaseReady } from '../supabase';

const LoginPage = ({ onLogin, onForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const ok = await onLogin(email.trim(), password);
    if (!ok) setError('Correo o contraseña inválidos. Inténtalo de nuevo.');
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F8FAFC' }}>
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="flex items-center justify-center gap-3 mb-7">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,#2563EB,#38BDF8)', boxShadow: '0 4px 14px rgba(37,99,235,0.35)' }}>
            <SafeIcon icon={FiZap} className="text-white text-lg" />
          </div>
          <div className="leading-tight">
            <span className="text-xl font-black tracking-tight block" style={{ color: '#0F172A' }}>Fullpower</span>
            <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#2563EB' }}>LeadFlow</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid #E2E8F0', boxShadow: '0 4px 24px rgba(15,23,42,0.07)' }}>
          <h2 className="text-lg font-bold mb-1" style={{ color: '#0F172A' }}>Inicia sesión en tu cuenta</h2>
          <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>Ingresa tus datos para continuar</p>

          {!isSupabaseReady && (
            <div className="mb-4 px-3 py-2.5 rounded-lg text-xs font-medium"
              style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }}>
              <strong>Modo local activo.</strong> Supabase no está configurado. Usando datos de prueba.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>
                Correo electrónico
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#94A3B8' }} />
                <input
                  type="email" required autoComplete="email"
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg text-sm outline-none transition-all"
                  style={{ border: '1px solid #E2E8F0', color: '#0F172A', backgroundColor: '#F8FAFC' }}
                  placeholder="usuario@empresa.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  onFocus={e => { e.target.style.borderColor = '#2563EB'; e.target.style.backgroundColor = '#fff'; }}
                  onBlur={e => { e.target.style.borderColor = '#E2E8F0'; e.target.style.backgroundColor = '#F8FAFC'; }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider" style={{ color: '#64748B' }}>
                  Contraseña
                </label>
                {isSupabaseReady && onForgotPassword && (
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    className="text-xs font-semibold transition-colors"
                    style={{ color: '#2563EB' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#1D4ED8'}
                    onMouseLeave={e => e.currentTarget.style.color = '#2563EB'}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#94A3B8' }} />
                <input
                  type={showPw ? 'text' : 'password'} required autoComplete="current-password"
                  className="w-full pl-9 pr-10 py-2.5 rounded-lg text-sm outline-none transition-all"
                  style={{ border: '1px solid #E2E8F0', color: '#0F172A', backgroundColor: '#F8FAFC' }}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  onFocus={e => { e.target.style.borderColor = '#2563EB'; e.target.style.backgroundColor = '#fff'; }}
                  onBlur={e => { e.target.style.borderColor = '#E2E8F0'; e.target.style.backgroundColor = '#F8FAFC'; }}
                />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: '#94A3B8' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#2563EB'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}>
                  <SafeIcon icon={showPw ? FiEyeOff : FiEye} className="text-sm" />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm"
                style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#EF4444' }}>
                <SafeIcon icon={FiAlertCircle} className="text-base flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit" disabled={loading}
              className="w-full py-2.5 rounded-lg text-white font-bold text-sm transition-all mt-1 disabled:opacity-70"
              style={{ backgroundColor: '#2563EB', boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = '#1D4ED8'; }}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563EB'}
            >
              {loading ? 'Iniciando sesión…' : 'Iniciar sesión'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;